import { motion, useInView } from 'framer-motion';
import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Cpu,
  Zap,
  Globe,
  Server,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const getWorkflowSteps = (t: any) => [
  {
    icon: Cpu,
    title: t('landing.workflowViz.step1.title'),
    description: t('landing.workflowViz.step1.description'),
    color: 'from-blue-500 to-blue-600',
    delay: 0
  },
  {
    icon: Server,
    title: t('landing.workflowViz.step2.title'),
    description: t('landing.workflowViz.step2.description'),
    color: 'from-emerald-500 to-emerald-600',
    delay: 0.2
  },
  {
    icon: Zap,
    title: t('landing.workflowViz.step3.title'),
    description: t('landing.workflowViz.step3.description'),
    color: 'from-purple-500 to-purple-600',
    delay: 0.4
  },
  {
    icon: Globe,
    title: t('landing.workflowViz.step4.title'),
    description: t('landing.workflowViz.step4.description'),
    color: 'from-pink-500 to-pink-600',
    delay: 0.6
  }
];

export function WorkflowVisualization() {
  const { t } = useTranslation();
  const workflowSteps = getWorkflowSteps(t);
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });
  const [activeStep, setActiveStep] = useState(0);

  // Auto-play animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % workflowSteps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={containerRef} className="relative py-24 overflow-hidden">
      {/* OpenClaw Logo Background */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <motion.img
          src="/openclaw.svg"
          alt=""
          className="w-[50%] h-auto opacity-[0.02] dark:opacity-[0.03]"
          animate={{ rotate: 360 }}
          transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
        />
      </motion.div>

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-emerald-50/30 dark:via-emerald-950/10 to-background" />

      <div className="container mx-auto px-4 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            {t('landing.workflowViz.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            {t('landing.workflowViz.title')}{' '}
            <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
              {t('landing.workflowViz.titleHighlight')}
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('landing.workflowViz.subtitle')}
          </p>
        </motion.div>

        {/* Workflow Steps */}
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-4 gap-6 relative">
            {/* Connection Lines */}
            <div className="hidden md:block absolute top-20 left-0 right-0 h-0.5">
              {workflowSteps.map((_, idx) => idx < workflowSteps.length - 1 && (
                <React.Fragment key={idx}>
                  <motion.div
                    className="absolute h-full bg-gradient-to-r from-emerald-500/50 to-transparent"
                    style={{
                      left: `${(idx * 100) / 3 + 12.5}%`,
                      width: `${100 / 3 - 12.5}%`
                    }}
                    initial={{ scaleX: 0 }}
                    animate={isInView ? { scaleX: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.8 + idx * 0.2 }}
                  />
                  {/* Animated Data Dots */}
                  <motion.div
                    className="absolute w-3 h-3 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/50"
                    style={{
                      top: '-5px',
                      left: `${(idx * 100) / 3 + 12.5}%`,
                    }}
                    animate={{
                      left: [
                        `${(idx * 100) / 3 + 12.5}%`,
                        `${((idx + 1) * 100) / 3 + 12.5}%`
                      ]
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: 'linear',
                      delay: idx * 0.8
                    }}
                  />
                </React.Fragment>
              ))}
            </div>

            {workflowSteps.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = activeStep === idx;

              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: step.delay }}
                  onMouseEnter={() => setActiveStep(idx)}
                  className="relative"
                >
                  <div className={`
                    bg-background border-2 rounded-2xl p-6 transition-all duration-300 cursor-pointer
                    ${isActive ? 'border-emerald-500 shadow-2xl shadow-emerald-500/20 -translate-y-2' : 'border-border hover:border-emerald-500/50'}
                  `}>
                    {/* Step Number */}
                    <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-emerald-500 text-white font-bold text-sm flex items-center justify-center shadow-lg">
                      {idx + 1}
                    </div>

                    {/* Icon */}
                    <motion.div
                      className={`w-16 h-16 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 shadow-lg`}
                      animate={isActive ? { scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      <StepIcon className="w-8 h-8 text-white" />
                    </motion.div>

                    {/* Content */}
                    <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>

                    {/* Checkmark */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={isActive ? { scale: 1 } : { scale: 0 }}
                      className="absolute bottom-4 right-4"
                    >
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    </motion.div>
                  </div>

                  {/* Arrow - Mobile */}
                  {idx < workflowSteps.length - 1 && (
                    <div className="md:hidden flex justify-center my-4">
                      <ArrowRight className="w-6 h-6 text-emerald-500" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Time Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 1 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30">
              <Zap className="w-5 h-5" />
              <span className="font-bold text-lg">{t('landing.workflowViz.timeLabel')}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
